/* src/render/globeView.ts — the planet at the top of the zoom ladder.
 *
 * Zoom all the way out of the flat city and this fades in: a globe with country borders,
 * Australian states, names, and a gold dot for every surveyed cell. It is navigation, not
 * simulation — drag to spin, wheel to zoom, and diving back in hands worldView a lon/lat to
 * travel the flat world to. Renders into the shared canvas with its own scene and camera,
 * exactly the interior view's pattern.
 *
 * The texture is rasterised by the context worker from the baked Natural Earth packs; labels
 * are pooled sprites (render/labels.ts), chosen per frame by zoom band and horizon.
 */

import {
  AdditiveBlending,
  BackSide,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Points,
  PointsMaterial,
  Raycaster,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  type WebGLRenderer,
} from 'three';
import { AU_CITIES } from '../core/cities.ts';
import type { LabelPoint } from '../workers/protocol.ts';
import { LabelPool, type LabelItem } from './labels.ts';

const DEG = Math.PI / 180;
const MIN_D = 1.17;
const MAX_D = 3.4;
const DRAG_THRESHOLD_PX = 6;

/** lon/lat → unit sphere, matching Three's SphereGeometry UV layout with the flipped-Y
 * equirect texture the worker bakes. Verified against the shape of Australia, which does not
 * lie about being mirrored. */
export function lonLatToVec3(lon: number, lat: number, radius = 1): Vector3 {
  const phi = (90 - lat) * DEG;
  const theta = (lon + 180) * DEG;
  return new Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}

/** The inverse, for turning a picked sphere point back into a place on Earth. */
export function vec3ToLonLat(v: Vector3): [number, number] {
  const r = v.length() || 1;
  const lat = 90 - Math.acos(v.y / r) / DEG;
  const lon = Math.atan2(v.z, -v.x) / DEG - 180;
  return [((lon + 540) % 360) - 180, lat];
}

export interface GlobeView {
  readonly active: boolean;
  /** Current focus and zoom, for seams and tests. */
  readonly focus: { lon: number; lat: number; d: number };
  enter: (lon: number, lat: number) => void;
  exit: () => void;
  update: (dt: number) => void;
  render: (renderer: WebGLRenderer) => void;
  /** Set once per session of wheeling in past the closest zoom; read-and-clear. */
  consumeDive: () => { lon: number; lat: number } | null;
  setSurveyDots: (points: ReadonlyArray<readonly [number, number]>) => void;
  requestTexture: (
    bake: (w: number, h: number) => Promise<{
      bitmap: ImageBitmap;
      countries: readonly LabelPoint[];
      states: readonly LabelPoint[];
    }>,
    /** When given, the texture uploads to the GPU immediately on arrival (renderer.initTexture)
     * — call this from an idle moment so the first globe entry pays nothing. */
    uploadNow?: WebGLRenderer,
  ) => void;
  dispose: () => void;
}

export function createGlobeView(canvas: HTMLCanvasElement): GlobeView {
  const scene = new Scene();
  scene.background = new Color(0x060a12);

  const camera = new PerspectiveCamera(45, 1, 0.01, 60);

  // Stars: one Points cloud far behind the planet — cheap and it sells "space".
  const starPositions: number[] = [];
  let seed = 987654321;
  const rand = (): number => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  for (let i = 0; i < 900; i += 1) {
    const v = new Vector3(rand() * 2 - 1, rand() * 2 - 1, rand() * 2 - 1);
    if (v.lengthSq() < 0.05) continue;
    v.normalize().multiplyScalar(38 + rand() * 14);
    starPositions.push(v.x, v.y, v.z);
  }
  const starGeometry = new BufferGeometry();
  starGeometry.setAttribute('position', new Float32BufferAttribute(starPositions, 3));
  const starMaterial = new PointsMaterial({
    color: 0xcfd8e6, size: 0.06, sizeAttenuation: true, depthWrite: false,
  });
  scene.add(new Points(starGeometry, starMaterial));

  // The planet. Unlit on purpose: the texture is the cartography, flat and legible.
  const sphereGeometry = new SphereGeometry(1, 96, 64);
  const sphereMaterial = new MeshBasicMaterial({ color: 0x28455c });
  const sphere = new Mesh(sphereGeometry, sphereMaterial);
  scene.add(sphere);

  // A whisper of atmosphere at the limb.
  const atmosphereGeometry = new SphereGeometry(1.035, 64, 48);
  const atmosphereMaterial = new MeshBasicMaterial({
    color: 0x6fa8d4,
    side: BackSide,
    transparent: true,
    opacity: 0.28,
    blending: AdditiveBlending,
    depthWrite: false,
  });
  scene.add(new Mesh(atmosphereGeometry, atmosphereMaterial));

  // Surveyed cells: small gold beads riding just above the surface.
  const dotGeometry = new SphereGeometry(0.0042, 6, 5);
  const dotMaterial = new MeshBasicMaterial({ color: 0xffd257 });
  let dots: InstancedMesh | null = null;
  const dotGroup = new Group();
  scene.add(dotGroup);

  const labels = new LabelPool(scene, 26);

  let countries: readonly LabelPoint[] = [];
  let states: readonly LabelPoint[] = [];
  let textureRequested = false;

  let active = false;
  let focusLon = 149;
  let focusLat = -30;
  let targetLon = focusLon;
  let targetLat = focusLat;
  let d = 2.6;
  let targetD = 2.6;
  let dive: { lon: number; lat: number } | null = null;

  /* ── Input ─────────────────────────────────────────────────────────────────────────── */

  let dragging = false;
  let passedThreshold = false;
  let lastX = 0;
  let lastY = 0;
  let downX = 0;
  let downY = 0;

  const raycaster = new Raycaster();
  const pointer = new Vector2();

  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    dragging = true;
    passedThreshold = false;
    lastX = downX = e.clientX;
    lastY = downY = e.clientY;
    canvas.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    if (!passedThreshold) {
      if (Math.hypot(e.clientX - downX, e.clientY - downY) <= DRAG_THRESHOLD_PX) return;
      passedThreshold = true;
    }
    // Degrees per pixel scales with zoom so a drag feels the same at every altitude.
    const speed = 0.11 * (d - 1);
    targetLon -= dx * speed;
    targetLat = Math.max(-80, Math.min(80, targetLat + dy * speed));
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    if (passedThreshold) return;

    // A clean click: spin the globe to the place under the cursor.
    const rect = canvas.getBoundingClientRect();
    pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hit = raycaster.intersectObject(sphere, false)[0];
    if (!hit) return;
    const [lon, lat] = vec3ToLonLat(hit.point);
    targetLon = lon;
    targetLat = Math.max(-80, Math.min(80, lat));
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    targetD *= Math.exp(e.deltaY * 0.0011);
    if (targetD > MAX_D) targetD = MAX_D;
    if (targetD < MIN_D) {
      targetD = MIN_D;
      // Wheeling in at the deck: hand the focus back to the flat world.
      if (e.deltaY < 0 && d < MIN_D * 1.06) {
        dive = { lon: focusLon, lat: focusLat };
      }
    }
  };

  const attach = (): void => {
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
  };
  const detach = (): void => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerUp);
    canvas.removeEventListener('wheel', onWheel);
  };

  /* ── Labels ────────────────────────────────────────────────────────────────────────── */

  const camDir = new Vector3();

  const updateLabels = (): void => {
    camera.getWorldDirection(camDir);
    const items: LabelItem[] = [];
    const size = 0.05 * (d - 0.9);

    const consider = (pts: readonly LabelPoint[], lift: number, cap: number, scale = 1): void => {
      let used = 0;
      for (const p of pts) {
        if (used >= cap || items.length >= 26) break;
        const v = lonLatToVec3(p.lon, p.lat, 1.01 + lift);
        // Horizon cull: labels on the far side (or edge-on) hide.
        if (v.dot(camDir) > -0.22) continue;
        items.push({ text: p.n, x: v.x, y: v.y, z: v.z, size: size * scale });
        used += 1;
      }
    };

    if (d > 2.0) {
      consider(countries, 0.02, 22);
    } else {
      // The one state name long enough to bury the city it contains.
      const stateNames = states.map((s) =>
        s.n === 'Australian Capital Territory' ? { ...s, n: 'ACT' } : s,
      );
      consider(stateNames, 0.02, 9, 0.85);
      const cities: LabelPoint[] = AU_CITIES
        .filter((c) => !c.name.includes('('))
        .map((c) => ({ n: c.name, lon: c.lon, lat: c.lat }));
      consider(cities, 0.012, d < 1.45 ? 15 : 8, 0.6);
      consider(countries, 0.02, 2);
    }
    labels.show(items);
  };

  /* ── The view object ───────────────────────────────────────────────────────────────── */

  const view: GlobeView = {
    get active(): boolean {
      return active;
    },

    get focus(): { lon: number; lat: number; d: number } {
      return { lon: focusLon, lat: focusLat, d };
    },

    enter(lon: number, lat: number): void {
      if (active) return;
      active = true;
      focusLon = targetLon = lon;
      focusLat = targetLat = Math.max(-80, Math.min(80, lat));
      // Arrive just off the deck and drift gently out: the fade lands on a view that still
      // reads as "the place I was over", and the zoom-out continues under the player's hand
      // instead of jumping to a continent-wide frame.
      d = 1.22;
      targetD = 1.55;
      dive = null;
      attach();
    },

    exit(): void {
      if (!active) return;
      active = false;
      labels.hide();
      detach();
      // The planet texture stays resident on purpose. It was disposed here once to save
      // ~45 MB of GPU memory, but re-uploading 4096×2048 is a one-frame stall the profiler
      // caught red-handed on every globe entry — the memory is cheaper than the hitch.
    },

    update(dt: number): void {
      const k = Math.min(1, dt * 7);
      // Shortest-way longitude chase, so crossing the antimeridian never spins the long way.
      let dLon = targetLon - focusLon;
      dLon = ((dLon + 540) % 360) - 180;
      focusLon += dLon * k;
      focusLat += (targetLat - focusLat) * k;
      d += (targetD - d) * Math.min(1, dt * 6);

      const eye = lonLatToVec3(focusLon, focusLat, d);
      camera.position.copy(eye);
      camera.up.set(0, 1, 0);
      camera.lookAt(0, 0, 0);

      const c = canvas;
      const aspect = (c.clientWidth || 1) / Math.max(1, c.clientHeight || 1);
      if (Math.abs(camera.aspect - aspect) > 1e-3) {
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
      }
      updateLabels();
    },

    render(renderer: WebGLRenderer): void {
      renderer.render(scene, camera);
    },

    consumeDive(): { lon: number; lat: number } | null {
      const out = dive;
      dive = null;
      return out;
    },

    setSurveyDots(points: ReadonlyArray<readonly [number, number]>): void {
      if (dots) {
        dotGroup.remove(dots);
        dots.dispose();
        dots = null;
      }
      if (points.length === 0) return;
      const mesh = new InstancedMesh(dotGeometry, dotMaterial, points.length);
      const m = new Matrix4();
      for (let i = 0; i < points.length; i += 1) {
        const p = points[i];
        if (!p) continue;
        const v = lonLatToVec3(p[0], p[1], 1.004);
        m.makeTranslation(v.x, v.y, v.z);
        mesh.setMatrixAt(i, m);
      }
      mesh.instanceMatrix.needsUpdate = true;
      dotGroup.add(mesh);
      dots = mesh;
    },

    requestTexture(bake, uploadNow): void {
      if (textureRequested) return;
      textureRequested = true;
      bake(4096, 2048)
        .then(({ bitmap, countries: c, states: s }) => {
          const texture = new Texture(bitmap);
          texture.colorSpace = SRGBColorSpace;
          texture.needsUpdate = true;
          sphereMaterial.map = texture;
          sphereMaterial.color.set(0xffffff);
          sphereMaterial.needsUpdate = true;
          countries = c;
          states = s;
          if (uploadNow) uploadNow.initTexture(texture);
        })
        .catch((err: unknown) => {
          // The globe still works as a blue sphere with city labels; degrade, don't die.
          textureRequested = false;
          console.warn('[globe] texture bake failed', err);
        });
    },

    dispose(): void {
      detach();
      labels.dispose();
      starGeometry.dispose();
      starMaterial.dispose();
      sphereGeometry.dispose();
      sphereMaterial.map?.dispose();
      sphereMaterial.dispose();
      atmosphereGeometry.dispose();
      atmosphereMaterial.dispose();
      dotGeometry.dispose();
      dotMaterial.dispose();
      if (dots) dots.dispose();
    },
  };

  return view;
}
